import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Stack from "components/common/Stack";
import Box from "components/common/Box";
import Loader from "components/common/Loader";
import GraphDashboard from "../Dashboard/GraphDashboard";

import { masterApis } from "stores/master/masterApis";
import { commonActions } from "stores/common/commonActions";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IconButton from "components/common/IconButton";
import Text from "components/common/Text";

const NaviaHeader = () => (
  <Stack sx={{ backgroundColor: "#fff", borderBottom: "1px solid #eee" }}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      px={{ xs: 2, sm: 4, md: 8, lg: 10 }}
      py={{ xs: 1, sm: 1.25 }}
    >
      {/* Navia logo */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <img src="/svgs/navia-logo.svg" width={120} alt="Navia-logo" />
      </Box>

      {/* Powered by Jarvis */}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ whiteSpace: "nowrap" }}>
        <Text sx={{ fontSize: 13, color: "#444444", mr: 2 }}>
          Powered by
        </Text>
        <img src="/jarvis-logo.svg" width={70} alt="Jarvis-logo" />
      </Stack>
    </Stack>

    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      px={{ xs: 2, sm: 4, md: 8, lg: 10 }}
      py={2}
      sx={{ backgroundColor: "#EBEBEB" }}
    >
      <IconButton
        onClick={() => {
          window.location.href = "https://open.navia.co.in/index-navia.php";
        }}
        sx={{
          backgroundColor: "#ADADAC",
          color: "#FFFFFF",
          height: 20,
          width: 20,
          borderRadius: "4px",
          p: 0,
          mr: 1,
          "&:hover": { backgroundColor: "#9A9A99" },
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 14 }} />
      </IconButton>

      <Box sx={{ fontSize: 14, fontWeight: 600, color: "#757575" }}>
        Portfolio Healthcheck
      </Box>
    </Stack>
  </Stack>
);

function PHCIframe() {
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();

  const reduxPortfolioId = useSelector((state) => state.common.portfolioId);

  const searchParams = new URLSearchParams(location.search);
  const actId =
    searchParams.get("actId") ||
    searchParams.get("actid") ||
    searchParams.get("ACTID");

  useEffect(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("legacyToken");
  }, []);

  let sessionId = params.token || localStorage.getItem("token") || null;
  let portfolioIdFromUrl = params.id || reduxPortfolioId || null;

  // Navia detection
  if (location.pathname.includes("/phc")) {
    const rawBeforePhc = location.pathname.split("/phc")[0];
    sessionId = decodeURIComponent(rawBeforePhc.slice(1));
    portfolioIdFromUrl = null; // Navia does not send portfolioId
  }

  const naviaSource = useSelector((state) => state.common?.source);
  const showHeader = naviaSource === "navia";

  // const isNavia =
  //   naviaSource === "navia" ||
  //   location.pathname.includes("/phc") ||
  //   !!actId;

  const [isLoading, setIsLoading] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [noHoldings, setNoHoldings] = useState(false);
  const [error, setError] = useState(null);

  const hasRunRef = useRef(false);

  useEffect(() => {
    const init = async () => {
      if (hasRunRef.current) return;
      hasRunRef.current = true;

      try {
        setIsLoading(true);

        if (actId) {
          if (!sessionId) {
            dispatch(
              commonActions.setToast({
                message: "Missing Navia session token",
              }),
            );
            return;
          }

          const res = await masterApis.getNaviaCustomer(sessionId, actId);

          //  No holdings case
          if (Array.isArray(res?.holdings) && res.holdings.length === 0) {
            setNoHoldings(true);
            setIsLoading(false);
            return;
          }

          const authToken = res?.result?.token;
          const portfolioId = res?.result?.portfolioId;

          if (!authToken || !portfolioId) {
            dispatch(
              commonActions.setToast({
                message: "Invalid Navia response",
              }),
            );
            return;
          }

          dispatch(
            commonActions.setNaviaDetail({
              token: authToken,
              portfolioId,
              isCkycDone: res.result.isCkycDone,
              source: "navia",
            }),
          );

          localStorage.setItem("token", authToken);
          localStorage.setItem("legacyToken", authToken);

          setIsTokenValid(true);
          return;
        }

        if (!sessionId || !portfolioIdFromUrl) {
          dispatch(
            commonActions.setToast({
              message: "Invalid PHC link",
            }),
          );
        }

        localStorage.setItem("token", sessionId);
        localStorage.setItem("legacyToken", sessionId);

        setIsTokenValid(true);
      } catch (err) {
        console.error("PHC iframe error:", err);
        setError(err.message || "Session expired");
        localStorage.removeItem("token");
        setIsTokenValid(false);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [sessionId, portfolioIdFromUrl, actId, dispatch, location.pathname]);

  if (isLoading) return <Loader loading />;

  if (error) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#d32f2f",
        }}
      >
        {error}
      </Box>
    );
  }

  //  Show "No Holdings" UI
  if (noHoldings) {
    return (
      <Stack
        sx={{
          height: "100vh",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          padding: 5,
        }}
      >
        <Box sx={{ mb: 2, fontSize: 20, fontWeight: "bold" }}>
          No Holdings Found
        </Box>
        <Box sx={{ mb: 4 }}>
          There are no holdings associated with this account.
        </Box>
        <button
          style={{
            padding: "10px 20px",
            backgroundColor: "#1976d2",
            color: "#fff",
            border: "none",
            borderRadius: 4,
            cursor: "pointer",
          }}
          onClick={() => {
            window.location.href = "https://open.navia.co.in/index-navia.php";
          }}
        >
          Go Back to Navia Website
        </button>
      </Stack>
    );
  }
  const isShortUrl = !actId && !location.pathname.includes("/phc");
  return (
    <>
      {showHeader && <NaviaHeader />}

      <Stack sx={{ backgroundColor: "#F2F0EF", p: 0 }}>
        {isTokenValid && (
          <GraphDashboard
            portfolioId={reduxPortfolioId || portfolioIdFromUrl}
            isShortUrl={isShortUrl}
          />
        )}
      </Stack>
    </>
  );
}

export default PHCIframe;
